/**
 * Arabic folding — §3.5.
 *
 * L14: this must stay BYTE-IDENTICAL to the build script's `normalise_arabic` /
 * `skeleton_arabic`. The index in search.json is folded at build time and queries are
 * folded here at runtime. Divergence returns ZERO RESULTS SILENTLY — it does not
 * raise. `arabic.test.ts` pins both directions: it asserts the codepoints of every
 * table, and it folds the real search.json display text and asserts it reproduces the
 * stored `n` and `s` fields exactly.
 *
 * L24: every Arabic character class here is written as codepoint escapes, never as a
 * literal Arabic glyph. A literal class bidi-reorders in the source file into a class
 * that matches every letter, silently.
 */

// ---------------------------------------------------------------------------
// Named codepoints. Referring to these by name rather than by glyph is what keeps
// the tables readable in an LTR editor and auditable by a test.
// ---------------------------------------------------------------------------

export const CP = {
  HAMZA: 0x0621, // ء
  ALEF_MADDA: 0x0622, // آ
  ALEF_HAMZA_ABOVE: 0x0623, // أ
  WAW_HAMZA: 0x0624, // ؤ
  ALEF_HAMZA_BELOW: 0x0625, // إ
  YEH_HAMZA: 0x0626, // ئ
  ALEF: 0x0627, // ا
  TEH_MARBUTA: 0x0629, // ة
  TATWEEL: 0x0640, // ـ
  WAW: 0x0648, // و
  ALEF_MAKSURA: 0x0649, // ى
  YEH: 0x064a, // ي
  SUPERSCRIPT_ALEF: 0x0670, // ٰ  (dagger alef)
  ALEF_WASLA: 0x0671, // ٱ
  ALEF_WAVY_HAMZA_ABOVE: 0x0672, // ٲ
  ALEF_WAVY_HAMZA_BELOW: 0x0673, // ٳ
  DOTLESS_BEH: 0x066e, // ٮ
  HEH: 0x0647, // ه
  SMALL_HIGH_YEH: 0x06e7, // ۧ
  SMALL_WAW: 0x06e5, // ۥ
  SMALL_YEH: 0x06e6, // ۦ
} as const

const ch = (cp: number) => String.fromCodePoint(cp)

// ---------------------------------------------------------------------------
// Step 1 — strip
// ---------------------------------------------------------------------------

/**
 * Inclusive ranges of codepoints deleted outright.
 *
 * Two deliberate carve-outs are visible in the range boundaries themselves:
 *
 *  - U+0670 superscript (dagger) alef is NOT stripped — it is a long ā and is folded
 *    to a full alef in step 3. Deleting it made `ذلك الكتاب` unfindable. It sits
 *    between the 064B–065F and 06D6–06E6 ranges, so no range covers it.
 *
 *  - U+06E7 small high yeh is NOT stripped — in this mushaf it marks a *pronounced*
 *    yeh, and stripping it lost 12 of Ibrāhīm's 63 ayat. That is why the 06D6 run is
 *    cut in two, resuming at 06E8. Note U+06E5/U+06E6 (small waw / small yeh) mark
 *    the *silent* vowel of لَهُۥ / بِهِۦ, which a reader types without, so those stay
 *    stripped.
 *
 * NEVER add U+0020. Stripping real spaces merges adjacent words and corrupts every
 * phrase query.
 */
export const STRIP_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0610, 0x061a], // Qurʾānic annotation signs
  [0x064b, 0x065f], // harakat, sukun, small vowel marks
  [0x0640, 0x0640], // tatweel
  [0x06d6, 0x06e6], // waqf, sajdah, small high/low letters — stops short of 06E7
  [0x06e8, 0x06ed], // the rest of that run, resuming past U+06E7
  [0x200c, 0x200f], // ZWNJ, ZWJ, LRM, RLM — invisible, but they break a substring
  //                   match that straddles them
  [0x00a0, 0x00a0], // no-break space
]

function isStripped(cp: number): boolean {
  for (const [lo, hi] of STRIP_RANGES) {
    if (cp >= lo && cp <= hi) return true
  }
  return false
}

function strip(input: string): string {
  let out = ''
  for (const c of input) {
    if (!isStripped(c.codePointAt(0)!)) out += c
  }
  return out
}

// ---------------------------------------------------------------------------
// Step 2 — two-character sequences, after strip and before the per-character fold
// ---------------------------------------------------------------------------

/**
 * Each entry is [first, [...allowed seconds], replacement].
 *
 * These exist because folding the two characters independently produces a phantom
 * letter that breaks every phrase running through the word:
 *
 *  - ALEF_MAKSURA + SUPERSCRIPT_ALEF → ALEF_MAKSURA. A dagger alef on an alef maqsura
 *    is a single long ā; folding each in turn produced `عليا` from `عَلَىٰ`.
 *    1,540 ayat carry it.
 *
 *  - HAMZA + {ALEF, SUPERSCRIPT_ALEF, ALEF_WAVY_HAMZA_ABOVE} → ALEF. This mushaf
 *    writes alef-madda as hamza + alef, so `ٱلْقُرْءَان` folded to `القرءان` while a
 *    reader types `القرآن` → `القران`. The hamza sat mid-word so no substring
 *    matched. All three alef spellings must be covered: U+0627 (1,075×), U+0672
 *    (8×, incl. 12:2 and 43:3) and U+0670. Matching only U+0627 left 12:2 and 43:3
 *    unreachable.
 *
 *  - HAMZA + YEH and YEH_HAMZA + YEH → YEH. The hamza seat in `إسرائيل`, spelled
 *    either way.
 *
 * Deliberately NOT done: a blanket "strip every bare hamza" — that folds `آلاء` to
 * `الا` and buries its 31 ayat under ~600 hits for `إلا`. Nor does the alef class
 * include U+0622/U+0623/U+0625: after a hamza those are a second *hamza*
 * (`ءَأَنذَرْتَهُمْ`), not a long ā, and folding them away merges the interrogative
 * into the plain verb.
 */
export const SEQUENCES: ReadonlyArray<readonly [number, readonly number[], number]> = [
  [CP.ALEF_MAKSURA, [CP.SUPERSCRIPT_ALEF], CP.ALEF_MAKSURA],
  [CP.HAMZA, [CP.ALEF, CP.SUPERSCRIPT_ALEF, CP.ALEF_WAVY_HAMZA_ABOVE], CP.ALEF],
  [CP.HAMZA, [CP.YEH], CP.YEH],
  [CP.YEH_HAMZA, [CP.YEH], CP.YEH],
]

function applySequences(input: string): string {
  const cps = Array.from(input, (c) => c.codePointAt(0)!)
  let out = ''
  for (let i = 0; i < cps.length; i += 1) {
    const first = cps[i]!
    const second = i + 1 < cps.length ? cps[i + 1]! : -1
    let matched = false
    for (const [a, seconds, replacement] of SEQUENCES) {
      if (first === a && seconds.includes(second)) {
        out += ch(replacement)
        i += 1 // consume the second character too
        matched = true
        break
      }
    }
    if (!matched) out += ch(first)
  }
  return out
}

// ---------------------------------------------------------------------------
// Step 3 — per-character fold
// ---------------------------------------------------------------------------

export const FOLD_MAP: ReadonlyMap<number, number> = new Map([
  [CP.SUPERSCRIPT_ALEF, CP.ALEF],
  [CP.ALEF_MADDA, CP.ALEF],
  [CP.ALEF_HAMZA_ABOVE, CP.ALEF],
  [CP.ALEF_HAMZA_BELOW, CP.ALEF],
  [CP.ALEF_WASLA, CP.ALEF],
  [CP.ALEF_WAVY_HAMZA_ABOVE, CP.ALEF],
  [CP.ALEF_WAVY_HAMZA_BELOW, CP.ALEF],
  [CP.ALEF_MAKSURA, CP.YEH],
  [CP.DOTLESS_BEH, CP.YEH],
  [CP.TEH_MARBUTA, CP.HEH],
  [CP.WAW_HAMZA, CP.WAW],
  [CP.YEH_HAMZA, CP.YEH],
  [CP.SMALL_HIGH_YEH, CP.YEH],
])

function foldChars(input: string): string {
  let out = ''
  for (const c of input) {
    const folded = FOLD_MAP.get(c.codePointAt(0)!)
    out += folded === undefined ? c : ch(folded)
  }
  return out
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * The folded form — what search.json stores in `n` and what a query is compared
 * against on the exact pass.
 */
export function normalise(input: string): string {
  if (!input) return ''
  return foldChars(applySequences(strip(input)))
}

/**
 * The long vowels the folded form still carries. Dropping them is what makes the
 * fallback index work: long vowels are exactly where Uthmani and modern orthography
 * disagree.
 */
export const SKELETON_REMOVED: ReadonlyArray<number> = [CP.ALEF, CP.WAW, CP.YEH]

const SKELETON_SET = new Set(SKELETON_REMOVED)

/**
 * The skeleton form — the folded form with [اوي] removed. Stored in search.json's
 * `s` and consulted only when the exact pass is empty (§9).
 *
 * A correction to the older documentation: folding U+0670 to a full alef does *not*
 * make `ذلك الكتاب` findable via the exact index — the opposite. The index holds
 * `ذالك` while a reader types `ذلك`, so every dagger-alef word (`الرحمن`, `ذلك`,
 * `السماوات`) misses the precise pass and is caught here instead. The behaviour is
 * right; the old framing was wrong.
 */
export function skeleton(input: string): string {
  const folded = normalise(input)
  let out = ''
  for (const c of folded) {
    if (!SKELETON_SET.has(c.codePointAt(0)!)) out += c
  }
  return out
}
